import type {
  AgentSuggestion,
  CategoryOptionsOutput,
  DayCategoryCompletion,
  ParseEmailExtracted,
} from '@travel-planner/shared/schemas';
import type { AgentPanelAdapter } from '../../model/types';
import type {
  DayOverride,
  GeneratorSelections,
} from '../../model/use-agent-panel-store';
import type { CategoryCardWarning } from './category-card';
import type { CategoryTabItem, GeneratorCategory } from './category-tab-bar';
import type { DayCategory } from './day-category-status';
import type { HashtagGroup, SelectedHashtag } from './rec-tuning-panel';
import { UTCDate } from '@date-fns/utc';
import { resolveThumbnailUrl } from '@products/entities/product';
import { AlertCircle, Car, Helicopter, PlaneLanding } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { joinCss, pickCss } from '@/shared/lib/css-utils';
import { Alert, Text } from '@/shared/ui';
import { useAgentPanelAdapter } from '../../model/adapter-context';
import { hashtagWeightFor } from '../../model/preference-emphasis';
import {
  DEFAULT_RECOMMENDATION_STATE,
  useAgentPanelStore,
} from '../../model/use-agent-panel-store';
import {
  useFitAnalysis,
  useResuggestForDay,
} from '../../model/use-fit-analysis';
import { BriefGapsDock, BriefGapsPanel } from '../recommendation';
import { CategoryCard } from './category-card';
import { CategoryProgressBar } from './category-progress-bar';
import { CategoryTabBar } from './category-tab-bar';
import { DayCategoryPanel } from './day-category-panel';
import {
  completionForCategory,
  DAY_CATEGORY_ORDER,
  dayGapCount,
  deriveCategoryStatus,
  deriveDurationDays,
  effectivePickedIdsForDay,
  pickedCountForCategory,
} from './day-category-status';
import { DayCategoryWarning } from './day-category-warning';
import { DayPickerBar } from './day-picker-bar';
import { DayPickerMenu } from './day-picker-menu';
import css from './options-step.module.scss';
import { RecTuningScreen } from './rec-tuning-screen';

const styles = pickCss(css, css.root);

const isItineraryAdapter = (
  adapter: AgentPanelAdapter | null
): adapter is AgentPanelAdapter<'itinerary'> =>
  adapter?.contextType === 'itinerary';

// Maps the per-tab category to the `contentCategory` enum the products
// router accepts. Drives the Browse-view catalog search filter.
const CATEGORY_KEY_TO_CONTENT_CATEGORY: Record<DayCategory, string> = {
  accommodation: 'Accommodation',
  activities: 'Activity',
  dining: 'Dining',
  transportation: 'Transportation',
};

// Per-category copy for the day-category panel meta label.
const CATEGORY_META_LABEL: Record<DayCategory, string> = {
  transportation: 'TRANSPORTATION',
  accommodation: 'STAY',
  activities: 'ACTIVITIES',
  dining: 'FOOD',
};

// Tab labels mirror the pen design (`oomtC`): Stay / Activities / Food /
// Transportation.
const CATEGORY_TAB_LABEL: Record<DayCategory, string> = {
  transportation: 'Transportation',
  accommodation: 'Stay',
  activities: 'Activities',
  dining: 'Food',
};

const SEARCH_MIN_LENGTH = 2;

// Multi-lingual product titles from the products router are either a plain
// string or an array of `{ lang, text }` rows. Flatten to a string.
const extractProductTitle = (title: unknown): string => {
  if (typeof title === 'string') return title;
  if (Array.isArray(title) && title.length > 0) {
    const rows = title as ReadonlyArray<{ lang?: string; text?: string }>;
    const en = rows.find((row) => row?.lang === 'en');
    const picked = en ?? rows[0];
    return typeof picked?.text === 'string' ? picked.text : '';
  }
  return '';
};

// products.list price is `z.unknown()` — accept numbers / numeric strings /
// `{ amount }` objects, drop anything else to null.
const extractProductPriceCents = (price: unknown): number | null => {
  if (typeof price === 'number' && Number.isFinite(price)) {
    return Math.round(price * 100);
  }
  if (typeof price === 'string') {
    const parsed = Number(price);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }
  if (price && typeof price === 'object' && 'amount' in price) {
    const parsed = Number((price as { amount: unknown }).amount);
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : null;
  }
  return null;
};

type ProductsListRow = {
  id: string;
  title: unknown;
  thumbnail: string | null;
  city: string | null;
  country: string | null;
  price: unknown;
  currency: string | null;
};

const FALLBACK_NOUN_BY_CATEGORY: Record<string, string> = {
  accommodation: 'hotel',
  activities: 'experience',
  dining: 'restaurant',
  transportation: 'transfer',
};

const productDisplayTitle = (
  product: AgentSuggestion,
  category: string
): string => {
  if (product.title && product.title.trim().length > 0) return product.title;
  const noun = FALLBACK_NOUN_BY_CATEGORY[category] ?? 'item';
  return `Untitled ${noun} · ${product.id.slice(-6)}`;
};

const mapProductRowToSuggestion = (row: ProductsListRow): AgentSuggestion => ({
  id: row.id,
  title: extractProductTitle(row.title) || 'Untitled product',
  subtitle: null,
  city: row.city,
  country: row.country,
  priceCents: extractProductPriceCents(row.price),
  currency: row.currency,
  imageUrl: resolveThumbnailUrl(row.thumbnail),
  durationMinutes: null,
  matchScore: 0,
  matchedHashtags: [],
  whyMatched: [],
  historyUses: 0,
});

// Transport icon glyph, inferred from high-signal title keywords.
const transportIconFor = (title: string) => {
  const lower = title.toLowerCase();
  if (lower.includes('helicopter') || lower.includes('flight')) {
    return Helicopter;
  }
  if (
    lower.includes('airport') ||
    lower.includes('arrival') ||
    lower.includes('departure')
  ) {
    return PlaneLanding;
  }
  return Car;
};

const optionsForCategory = (
  options: CategoryOptionsOutput['options'],
  category: DayCategory
): ReadonlyArray<AgentSuggestion> => {
  switch (category) {
    case 'transportation':
      return options.transportation;
    case 'accommodation':
      return options.accommodation;
    case 'activities':
      return options.activities;
    case 'dining':
      return options.dining;
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

const preselectedIdsForCategory = (
  preselected: CategoryOptionsOutput['preselected'],
  category: DayCategory
): ReadonlySet<string> => {
  switch (category) {
    case 'transportation':
      return new Set(preselected.transportationIds);
    case 'accommodation':
      return new Set(
        preselected.accommodationId ? [preselected.accommodationId] : []
      );
    case 'activities':
      return new Set(preselected.activityIds);
    case 'dining':
      return new Set(preselected.diningIds);
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

const selectedIdsForCategory = (
  selections: GeneratorSelections,
  category: DayCategory
): ReadonlySet<string> => {
  switch (category) {
    case 'transportation':
      return new Set(selections.transportationIds);
    case 'accommodation':
      return new Set(
        selections.accommodationId ? [selections.accommodationId] : []
      );
    case 'activities':
      return new Set(selections.activityIds);
    case 'dining':
      return new Set(selections.diningIds);
    default: {
      const _exhaustive: never = category;
      return _exhaustive;
    }
  }
};

const sumPicked = (
  options: CategoryOptionsOutput['options'],
  selections: GeneratorSelections
): { amount: number; currency: string | null } => {
  const ids = new Set<string>([
    ...selections.activityIds,
    ...(selections.accommodationId ? [selections.accommodationId] : []),
    ...selections.diningIds,
    ...selections.transportationIds,
  ]);
  let amount = 0;
  let currency: string | null = null;
  const all = [
    ...options.activities,
    ...options.accommodation,
    ...options.dining,
    ...options.transportation,
  ];
  for (const product of all) {
    if (!ids.has(product.id) || product.priceCents == null) continue;
    amount += product.priceCents;
    if (currency == null) currency = product.currency;
  }
  return { amount, currency };
};

const computeRemainingCents = (
  extracted: ParseEmailExtracted,
  pickedCents: number,
  pickedCurrency: string | null
): number | null => {
  if (!extracted.budgetTotal) return null;
  if (
    pickedCurrency != null &&
    extracted.budgetTotal.currency !== pickedCurrency
  ) {
    return null;
  }
  return extracted.budgetTotal.amount * 100 - pickedCents;
};

const cardWarningFor = (
  product: AgentSuggestion,
  selected: boolean,
  remainingCents: number | null,
  pickedCurrency: string | null
): CategoryCardWarning | null => {
  if (selected || product.priceCents == null) return null;
  if (
    pickedCurrency != null &&
    product.currency != null &&
    product.currency !== pickedCurrency
  ) {
    return 'mixed-currency';
  }
  if (remainingCents == null) return null;
  if (product.priceCents > remainingCents) return 'over-budget';
  return null;
};

// Day date label "Wed Oct 6", anchored on the brief start date.
const formatDayDate = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): string => {
  if (!extracted.startDate) return `Day ${dayIndex + 1}`;
  const base = new UTCDate(`${extracted.startDate}T00:00:00.000Z`);
  if (Number.isNaN(base.getTime())) return `Day ${dayIndex + 1}`;
  base.setUTCDate(base.getUTCDate() + dayIndex);
  return base.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

// Walk cumulative nights to map a day index onto its destination. For a
// brief like [Barcelona 5n, Madrid 4n, Seville 2n]:
//   Day 0..4  → Barcelona
//   Day 5..8  → Madrid
//   Day 9..10 → Seville
// Destinations with `nights == null` consume one day (so a brief that
// omits nights still surfaces every city). The earlier `destinations[dayIndex]`
// mapping silently fell back to destinations[0] for any day past the
// destinations.length boundary, which on a multi-night trip showed the
// wrong city on most days.
const resolveDayDestination = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): ParseEmailExtracted['destinations'][number] | null => {
  const destinations = extracted.destinations.filter(
    (d) => d.city || d.country
  );
  if (destinations.length === 0) return null;
  let cursor = 0;
  for (const dest of destinations) {
    const span = Math.max(1, dest.nights ?? 1);
    if (dayIndex < cursor + span) return dest;
    cursor += span;
  }
  // Day index falls beyond the brief's stated nights — anchor to the last
  // destination so the UI doesn't snap back to destination[0].
  return destinations[destinations.length - 1] ?? null;
};

const formatDayCity = (
  extracted: ParseEmailExtracted,
  dayIndex: number
): string | null => {
  const picked = resolveDayDestination(extracted, dayIndex);
  return picked?.city ?? picked?.country ?? null;
};

// Pill text for a category tab — "Picked" / "2/3" / "Missing".
const statusLabelFor = (
  completion: DayCategoryCompletion,
  picked: number,
  required: number
): string => {
  if (completion === 'complete') return 'Picked';
  if (completion === 'missing') return 'Missing';
  return `${picked}/${required}`;
};

// Noun phrase per category used in the day subtitle and gap-banner title.
// Pen `o1NXc` / `w8htb` use these plurals ("Activities done · Restaurants
// still open").
const CATEGORY_DAY_PLURAL: Record<DayCategory, string> = {
  accommodation: 'Stay',
  activities: 'Activities',
  dining: 'Restaurants',
  transportation: 'Transport',
};

// Per-category subtitle segment: "Stay done" / "Activities 2/3" /
// "Restaurants still open". Returns null for completed-and-quiet categories
// so the meta string only names what the consultant should pay attention to.
const daySubtitleSegment = (
  category: DayCategory,
  completion: DayCategoryCompletion,
  picked: number,
  required: number
): string | null => {
  const name = CATEGORY_DAY_PLURAL[category];
  if (completion === 'missing') return `${name} still open`;
  if (completion === 'partial') return `${name} ${picked}/${required}`;
  return null;
};

// Pen `w8htb` gap-banner title: "Restaurants missing on this day" /
// "Activities need more picks". Reads from the active category's
// completion state.
const buildGapTitle = (
  category: DayCategory,
  completion: DayCategoryCompletion
): string => {
  const name = CATEGORY_DAY_PLURAL[category];
  if (completion === 'missing') return `${name} missing on this day`;
  return `${name} need more picks`;
};

// Pen `w8htb` description: "Client asked for {interest} — boost {plural}
// picks with #{tags}". Falls back when the brief is light on signals.
const buildGapDescription = (
  category: DayCategory,
  interests: ReadonlyArray<string>,
  selectedHashtags: ReadonlyArray<{ tag: string }>
): string => {
  const noun = CATEGORY_DAY_PLURAL[category].toLowerCase();
  const interest = interests[0];
  const tagList = selectedHashtags
    .slice(0, 2)
    .map((h) => `#${h.tag}`)
    .join(' ');
  if (interest && tagList) {
    return `Client asked for ${interest} — boost ${noun} picks with ${tagList}.`;
  }
  if (interest) {
    return `Client asked for ${interest} — re-rank to surface stronger ${noun} options.`;
  }
  if (tagList) {
    return `Re-rank ${noun} picks using ${tagList}.`;
  }
  return `Re-rank ${noun} picks to better fit the brief.`;
};

export const OptionsStep = () => {
  const adapter = useAgentPanelAdapter();
  const generatorState = useAgentPanelStore((s) => s.generatorState);
  const setGeneratorState = useAgentPanelStore((s) => s.setGeneratorState);
  const setActiveDayIndex = useAgentPanelStore((s) => s.setActiveDayIndex);
  const setActiveCategory = useAgentPanelStore((s) => s.setActiveCategory);
  const setDayOverride = useAgentPanelStore((s) => s.setDayOverride);
  const dismissGap = useAgentPanelStore((s) => s.dismissGap);
  const close = useAgentPanelStore((s) => s.close);

  const buildFromSelectionsMutation =
    trpc.agentPanel.itineraryBuildFromSelections.useMutation();
  const setWeightsMutation =
    trpc.agentPanel.setRecommendationWeights.useMutation();

  const itineraryId = isItineraryAdapter(adapter)
    ? adapter.context.itineraryId
    : null;

  const fitAnalysis = useFitAnalysis(itineraryId);
  const { resuggest } = useResuggestForDay(itineraryId);

  // Pen `B3oRd` retired the AI/Browse toggle in favour of a unified
  // catalog-search bar at the panel top + a bottom "Browse the full catalog"
  // CTA. We keep one boolean to expand the recommended list into the full
  // pool when the consultant clicks the CTA.
  const [menuOpen, setMenuOpen] = useState(false);
  const [gapsExpanded, setGapsExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [showFullCatalog, setShowFullCatalog] = useState(false);
  const [tuningOpen, setTuningOpen] = useState(false);

  const isOptionsPhase = generatorState?.phase === 'options';
  const extracted = isOptionsPhase ? generatorState.extracted : null;
  const options = isOptionsPhase ? generatorState.options : null;
  const preselected = isOptionsPhase ? generatorState.preselected : null;
  const selections = isOptionsPhase ? generatorState.selections : null;
  const stagedRemovals = isOptionsPhase ? generatorState.stagedRemovals : [];
  const dayOverrides = isOptionsPhase ? generatorState.dayOverrides : null;
  const recommendation = isOptionsPhase ? generatorState.recommendation : null;
  const activeDayIndex = isOptionsPhase ? generatorState.activeDayIndex : 0;
  const activeCategory: DayCategory = isOptionsPhase
    ? generatorState.activeCategory
    : 'accommodation';
  const activeBuildStep = isOptionsPhase
    ? generatorState.activeBuildStep
    : ('day-picks' as const);

  const durationDays = useMemo(
    () => (extracted ? deriveDurationDays(extracted) : 1),
    [extracted]
  );

  // Per-day category status, derived live from picks (PRD FR-5). The
  // analysis `categoryStatus` is a baseline only.
  const categoryStatus = useMemo(() => {
    if (!selections) return [];
    return deriveCategoryStatus(durationDays, selections, dayOverrides ?? {});
  }, [durationDays, selections, dayOverrides]);

  const activeDayStatus = categoryStatus[activeDayIndex] ?? null;

  // Each day/category combo opens fresh in recommended mode — clear the
  // catalog expansion and any search term when the consultant navigates.
  useEffect(() => {
    setShowFullCatalog(false);
    setSearchInput('');
  }, [activeDayIndex, activeCategory]);

  // Open gaps surfaced by the analysis, minus the ones the consultant
  // already dismissed.
  const dismissedGaps = recommendation?.dismissedGaps ?? [];
  const visibleGaps = useMemo(() => {
    const gaps = fitAnalysis?.gaps ?? [];
    return gaps.filter((g) => !dismissedGaps.includes(g.id));
  }, [fitAnalysis, dismissedGaps]);

  // Hashtag library — the full org taxonomy from the CMS. Pen `TeHTQ`
  // renders chips in flow rows without group labels, so we collapse all
  // four `category_hashtag_category_enum` values (hotel / experience /
  // restaurant / landing) into one alphabetically-sorted, de-duplicated
  // pool. The earlier 3-bucket filter silently dropped `landing` tags.
  const hashtagsQuery = trpc.cms.hashtags.list.useQuery({});
  const hashtagLibrary = useMemo<ReadonlyArray<HashtagGroup>>(() => {
    const rows = hashtagsQuery.data ?? [];
    const uniqueNames = new Set<string>();
    for (const tag of rows) uniqueNames.add(tag.name);
    if (uniqueNames.size === 0) return [];
    return [{ group: '', tags: [...uniqueNames].sort() }];
  }, [hashtagsQuery.data]);

  const selectedHashtags = useMemo<ReadonlyArray<SelectedHashtag>>(
    () =>
      Object.entries(recommendation?.preferenceEmphasis ?? {}).map(
        ([tag, emphasis]) => ({
          tag,
          provenance: (recommendation?.preferenceProvenance?.[tag] ??
            'manual') as 'brief' | 'manual',
          weight: hashtagWeightFor(
            recommendation?.preferenceWeights,
            tag,
            emphasis
          ),
        })
      ),
    [recommendation]
  );

  // Server-side product search, scoped to the active category. The unified
  // search bar fires as soon as the consultant types past SEARCH_MIN_LENGTH —
  // no more view toggle.
  const searchQuery = searchInput.trim();
  const isSearching = searchQuery.length >= SEARCH_MIN_LENGTH;
  const productsSearchQuery = trpc.products.list.useQuery(
    {
      title: searchQuery,
      contentCategory: [CATEGORY_KEY_TO_CONTENT_CATEGORY[activeCategory]],
      limit: 20,
    },
    { enabled: isSearching }
  );
  const searchSuggestions = useMemo<ReadonlyArray<AgentSuggestion>>(() => {
    if (!productsSearchQuery.data) return [];
    return productsSearchQuery.data.data.map((row) =>
      mapProductRowToSuggestion(row as ProductsListRow)
    );
  }, [productsSearchQuery.data]);

  const updateSelections = useCallback(
    (next: GeneratorSelections) => {
      if (!isOptionsPhase || !options || !preselected || !extracted) return;
      setGeneratorState({
        phase: 'options',
        extracted,
        options,
        preselected,
        selections: next,
        stagedRemovals: stagedRemovals ?? [],
        dayOverrides: dayOverrides ?? {},
        recommendation: recommendation ?? DEFAULT_RECOMMENDATION_STATE,
        activeDayIndex,
        activeCategory,
        activeBuildStep,
      });
    },
    [
      isOptionsPhase,
      options,
      preselected,
      extracted,
      stagedRemovals,
      dayOverrides,
      recommendation,
      activeDayIndex,
      activeCategory,
      setGeneratorState,
    ]
  );

  // Per-day picks (activities / dining / transportation) write to
  // `dayOverrides[String(activeDayIndex)]`, mirroring the read path in
  // `pickedCountsForDay` / `effectivePickedIdsForDay`. Accommodation stays
  // in global `selections` — a single stay covers the whole trip.
  const toggleProduct = useCallback(
    (category: DayCategory, id: string) => {
      if (!selections) return;
      if (category === 'accommodation') {
        updateSelections({
          ...selections,
          accommodationId: selections.accommodationId === id ? null : id,
        });
        return;
      }
      const key =
        category === 'transportation'
          ? 'transportationIds'
          : category === 'activities'
            ? 'activityIds'
            : 'diningIds';
      const currentOverride = (dayOverrides ?? {})[String(activeDayIndex)];
      // First touch on this day: seed the override from the effective set
      // (override > global selections) so the consultant's prior global
      // picks aren't silently dropped when they switch to per-day mode.
      const baseList =
        currentOverride?.[key] !== undefined
          ? currentOverride[key]
          : selections[key];
      const nextList = baseList.includes(id)
        ? baseList.filter((x) => x !== id)
        : [...baseList, id];
      setDayOverride(activeDayIndex, {
        ...(currentOverride ?? {}),
        [key]: nextList,
      });
    },
    [selections, updateSelections, dayOverrides, activeDayIndex, setDayOverride]
  );

  // Toggling a hashtag must re-rank the product pool on the server. Without
  // this, the consultant can
  // tap chips all day and the AI Recommended list stays frozen on the
  // brief's original picks.
  const refreshRanking = useCallback(() => {
    if (!options || !itineraryId) return;
    const current = useAgentPanelStore.getState().generatorState;
    if (!current || current.phase !== 'options') return;
    setWeightsMutation
      .mutateAsync({
        itineraryId,
        options,
        categoryWeights: current.recommendation.categoryWeights,
        preferenceEmphasis: current.recommendation.preferenceEmphasis,
        dismissedPreferences: [...current.recommendation.dismissedPreferences],
      })
      .then((result) => {
        const gs = useAgentPanelStore.getState().generatorState;
        if (!gs || gs.phase !== 'options') return;
        setGeneratorState({ ...gs, options: result.options });
      })
      .catch(() => {
        // Re-rank failure is non-fatal — the store state still reflects
        // the consultant's pick; only the product order is stale.
      });
  }, [options, itineraryId, setWeightsMutation, setGeneratorState]);

  const handleAddHashtag = useCallback(
    (tag: string) => {
      useAgentPanelStore.getState().setHashtagWeight(tag, 90);
      refreshRanking();
    },
    [refreshRanking]
  );
  const handleRemoveHashtag = useCallback(
    (tag: string) => {
      useAgentPanelStore.getState().clearPreferenceEmphasis(tag);
      refreshRanking();
    },
    [refreshRanking]
  );

  const handleHashtagWeightChange = useCallback(
    (tag: string, value: number) => {
      useAgentPanelStore.getState().setHashtagWeight(tag, value);
      refreshRanking();
    },
    [refreshRanking]
  );

  // Browse-view inline swap: re-suggest products for the active day + category
  // via `resuggestForDay`, then persist atomically: merge new products into
  // the options pool, replace the category selections with the override IDs,
  // and store a day-level override for buildFromSelections.
  const handleResuggest = useCallback(async () => {
    if (
      !selections ||
      !extracted ||
      !options ||
      !preselected ||
      !recommendation
    )
      return;
    const city = formatDayCity(extracted, activeDayIndex) ?? 'the day';
    try {
      const result = await resuggest({
        dayIndex: activeDayIndex,
        intent: `Better ${CATEGORY_TAB_LABEL[activeCategory]} options for ${city}`,
        currentSelections: {
          activityIds: [...selections.activityIds],
          accommodationId: selections.accommodationId,
          diningIds: [...selections.diningIds],
          transportationIds: [...selections.transportationIds],
        },
      });
      if (result.length === 0) return;
      const ids = result.map((p: AgentSuggestion) => p.id);

      // Merge any returned products that aren't already in the options pool.
      const poolNow = options[activeCategory] as ReadonlyArray<AgentSuggestion>;
      const existingSet = new Set(poolNow.map((p) => p.id));
      const mergedPool = [
        ...poolNow,
        ...result.filter((p: AgentSuggestion) => !existingSet.has(p.id)),
      ];

      // Replace this category's global selections with the resuggested IDs so
      // the card list shows them as selected in recommended view.
      const nextSelections: GeneratorSelections =
        activeCategory === 'accommodation'
          ? { ...selections, accommodationId: ids[0] ?? null }
          : activeCategory === 'transportation'
            ? { ...selections, transportationIds: ids }
            : activeCategory === 'activities'
              ? { ...selections, activityIds: ids }
              : { ...selections, diningIds: ids };

      // Day-level override so buildFromSelections pins these picks to this day.
      const override: DayOverride =
        activeCategory === 'transportation'
          ? { transportationIds: ids }
          : activeCategory === 'activities'
            ? { activityIds: ids }
            : activeCategory === 'dining'
              ? { diningIds: ids }
              : { accommodationId: ids[0] ?? null };

      setGeneratorState({
        phase: 'options',
        extracted,
        options: { ...options, [activeCategory]: mergedPool },
        preselected,
        selections: nextSelections,
        stagedRemovals: stagedRemovals ?? [],
        dayOverrides: {
          ...(dayOverrides ?? {}),
          [String(activeDayIndex)]: override,
        },
        recommendation,
        activeDayIndex,
        activeCategory,
        activeBuildStep,
      });
    } catch {
      // Non-fatal; the existing picks stay.
    }
  }, [
    selections,
    extracted,
    options,
    preselected,
    recommendation,
    activeDayIndex,
    activeCategory,
    stagedRemovals,
    dayOverrides,
    activeBuildStep,
    resuggest,
    setGeneratorState,
  ]);

  const handleGenerate = useCallback(async () => {
    if (
      !extracted ||
      !selections ||
      !options ||
      !preselected ||
      !itineraryId ||
      !recommendation
    ) {
      return;
    }
    try {
      const result = await buildFromSelectionsMutation.mutateAsync({
        contextType: 'itinerary',
        itineraryId,
        extracted,
        selections,
        dayOverrides:
          dayOverrides && Object.keys(dayOverrides).length > 0
            ? dayOverrides
            : undefined,
      });
      setGeneratorState({
        phase: 'preview',
        extracted,
        options,
        preselected,
        selections,
        dayOverrides: dayOverrides ?? {},
        recommendation,
        schedule: result.schedule,
        versionId: result.versionId,
        durationDays: result.durationDays,
        estimatedTotal: result.estimatedTotal,
        activeBuildStep: 'send',
      });
    } catch {
      // Surface inline; consultant stays on the options step.
    }
  }, [
    extracted,
    selections,
    options,
    preselected,
    itineraryId,
    dayOverrides,
    recommendation,
    buildFromSelectionsMutation,
    setGeneratorState,
  ]);

  if (
    !isOptionsPhase ||
    !extracted ||
    !options ||
    !selections ||
    !preselected ||
    !activeDayStatus
  ) {
    return null;
  }

  const pickedTotals = sumPicked(options, selections);
  const remainingCents = computeRemainingCents(
    extracted,
    pickedTotals.amount,
    pickedTotals.currency
  );

  // Day-picker bar / menu.
  const dayCity = formatDayCity(extracted, activeDayIndex);
  const dayLabel = `Day ${activeDayIndex + 1}${dayCity ? ` · ${dayCity}` : ''}`;
  const activeDayGaps = dayGapCount(activeDayStatus);
  // Pen `o1NXc`: a comma-list of incomplete categories ("Activities still
  // open · Restaurants 2/3"). Complete categories drop out so the field
  // only carries what the consultant still has to act on.
  const subtitleSegments = DAY_CATEGORY_ORDER.map((category) => {
    const completion = completionForCategory(activeDayStatus, category);
    const { picked, required } = pickedCountForCategory(
      activeDayStatus,
      category
    );
    return daySubtitleSegment(category, completion, picked, required);
  }).filter((segment): segment is string => segment !== null);
  const dayMeta =
    subtitleSegments.length === 0
      ? 'All categories picked'
      : subtitleSegments.join(' · ');
  const menuEntries = categoryStatus.map((status) => ({
    dayIndex: status.dayIndex,
    city: formatDayCity(extracted, status.dayIndex),
    dateLabel: formatDayDate(extracted, status.dayIndex),
    gaps: dayGapCount(status),
  }));

  // Category tabs with per-day status pills.
  const tabs: ReadonlyArray<CategoryTabItem> = DAY_CATEGORY_ORDER.map(
    (category) => {
      const completion = completionForCategory(activeDayStatus, category);
      const { picked, required } = pickedCountForCategory(
        activeDayStatus,
        category
      );
      return {
        category: category as GeneratorCategory,
        label: CATEGORY_TAB_LABEL[category],
        status: completion,
        statusLabel: statusLabelFor(completion, picked, required),
      };
    }
  );

  // Card list for the active category. Pen `B3oRd` unifies the surface:
  //   - active search query → server search results
  //   - else `showFullCatalog` → full category pool (the bottom CTA path)
  //   - else recommended slice (preselected + selected) — the default
  //
  // The list is intentionally stable across days (same matcher pool), but
  // each card's `selected` state reads the day-specific effective pick set
  // so toggling on Day 1 doesn't pre-check Day 5.
  const fullCategoryOptions = optionsForCategory(options, activeCategory);
  // Multi-destination briefs surface products from every destination in a
  // single pool — scope the day's "AI Recommended" view to the active
  // destination's city so Day 1 (Barcelona) doesn't show Madrid activities
  // and vice versa. Stay + transportation are one-per-trip in v3, so they
  // skip the filter and keep the unified pool. Single-destination briefs
  // also skip the filter — the whole pool is already on-topic.
  const destinationsWithCity = extracted.destinations.filter(
    (d) => d.city || d.country
  );
  const shouldScopeByCity =
    destinationsWithCity.length > 1 &&
    (activeCategory === 'activities' || activeCategory === 'dining');
  const categoryOptions: ReadonlyArray<AgentSuggestion> = shouldScopeByCity
    ? (() => {
        const matchTarget = dayCity?.toLowerCase() ?? null;
        if (!matchTarget) return fullCategoryOptions;
        const scoped = fullCategoryOptions.filter(
          (p) => p.city?.toLowerCase() === matchTarget
        );
        // If filtering empties the list (matcher returned nothing for this
        // city), fall back to the full pool so the consultant still sees
        // something. The browse-full-catalog CTA is the explicit escape.
        return scoped.length > 0 ? scoped : fullCategoryOptions;
      })()
    : fullCategoryOptions;
  const preselectedIds = preselectedIdsForCategory(preselected, activeCategory);
  const globalSelectedIds = selectedIdsForCategory(selections, activeCategory);
  const dayPickedIds = effectivePickedIdsForDay(
    selections,
    dayOverrides ?? {},
    activeDayIndex,
    activeCategory
  );
  // Day-specific selected state — what the cards' check squares reflect.
  const selectedIds = dayPickedIds;
  // Recommended pool unions the matcher's preselected with every other
  // source so a product picked on Day 3 doesn't vanish on Day 4's view.
  const recommendedList: ReadonlyArray<AgentSuggestion> =
    categoryOptions.filter(
      (p) =>
        preselectedIds.has(p.id) ||
        globalSelectedIds.has(p.id) ||
        dayPickedIds.has(p.id)
    );
  const cardList: ReadonlyArray<AgentSuggestion> = isSearching
    ? searchSuggestions
    : (showFullCatalog ? [...categoryOptions] : [...recommendedList]).sort(
        (a, b) => b.matchScore - a.matchScore
      );

  const { picked: activePicked, required: activeRequired } =
    pickedCountForCategory(activeDayStatus, activeCategory);
  // Pen `B3oRd` meta label: "AI RECOMMENDED · ACTIVITIES" until the
  // consultant either searches or expands into the full pool.
  const isBrowseMode = isSearching || showFullCatalog;
  const metaLabel = `${
    isBrowseMode ? 'BROWSE CATALOG' : 'AI RECOMMENDED'
  } · ${CATEGORY_META_LABEL[activeCategory]}`;
  // Once the day has enough picks for the category, show a flat count —
  // "3 / 1 picked" reads as a broken fraction.
  const pickedLabel =
    activePicked >= activeRequired
      ? `${activePicked} picked`
      : `${activePicked} / ${activeRequired} picked`;

  // Inline fit warning for the active category.
  const activeCompletion = completionForCategory(
    activeDayStatus,
    activeCategory
  );
  const showWarning = activeCompletion !== 'complete';

  const isPending = buildFromSelectionsMutation.isPending;
  const errorMessage = buildFromSelectionsMutation.error?.message ?? null;
  const isGenerateDisabled = isPending || !itineraryId;

  // Footer progress caption.
  const remainingCategories = tabs.filter((t) => t.status !== 'complete');
  const progressCaption =
    remainingCategories.length === 0
      ? `Day ${activeDayIndex + 1} complete — preview the itinerary`
      : `${4 - remainingCategories.length} / 4 categories · finish ${remainingCategories
          .map((t) => t.label)
          .join(', ')} to confirm day`;

  // Gap-dock labels — short category names of open gaps.
  const dockLabels = visibleGaps
    .slice(0, 3)
    .map((g) => g.title)
    .filter(Boolean);

  if (tuningOpen) {
    return (
      <div className={styles()}>
        <RecTuningScreen
          hashtagLibrary={hashtagLibrary}
          onAddHashtag={handleAddHashtag}
          onClose={() => setTuningOpen(false)}
          onHashtagWeightChange={handleHashtagWeightChange}
          onRemoveHashtag={handleRemoveHashtag}
          selectedHashtags={selectedHashtags}
        />
      </div>
    );
  }

  return (
    <div className={styles()}>
      <DayPickerBar
        dayLabel={dayLabel}
        dayMeta={dayMeta}
        nextDisabled={activeDayIndex >= durationDays - 1}
        onNext={() => setActiveDayIndex(activeDayIndex + 1)}
        onOpenMenu={() => setMenuOpen((o) => !o)}
        onPrev={() => setActiveDayIndex(activeDayIndex - 1)}
        prevDisabled={activeDayIndex <= 0}
        tone={activeDayGaps === 0 ? 'ok' : 'attention'}
      />

      {menuOpen && (
        <DayPickerMenu
          activeDayIndex={activeDayIndex}
          days={menuEntries}
          onSelect={(dayIndex) => {
            setActiveDayIndex(dayIndex);
            setMenuOpen(false);
          }}
        />
      )}

      <CategoryProgressBar
        dayLabel={`Day ${activeDayIndex + 1}`}
        segments={tabs.map((t) => t.status)}
      />

      <CategoryTabBar
        activeCategory={activeCategory as GeneratorCategory}
        onSelect={(category) => setActiveCategory(category as DayCategory)}
        tabs={tabs}
      />

      <DayCategoryPanel
        browseCtaLabel={`Browse the full ${
          dayCity ?? 'catalog'
        } ${CATEGORY_TAB_LABEL[activeCategory].toLowerCase()} catalog`}
        metaLabel={metaLabel}
        onBrowseAll={isBrowseMode ? undefined : () => setShowFullCatalog(true)}
        onOpenTuning={() => setTuningOpen(true)}
        onSearchChange={setSearchInput}
        pickedLabel={pickedLabel}
        searchValue={searchInput}
        warning={
          showWarning ? (
            <DayCategoryWarning
              adjustLabel="Adjust"
              description={buildGapDescription(
                activeCategory,
                extracted.interests,
                selectedHashtags
              )}
              onAdjust={() => void handleResuggest()}
              title={buildGapTitle(activeCategory, activeCompletion)}
            />
          ) : undefined
        }
      >
        {cardList.length === 0 ? (
          <Text size="sm" variant="muted">
            {isSearching
              ? 'No catalog products match your search.'
              : 'No candidates returned for this category.'}
          </Text>
        ) : (
          cardList.map((product) => {
            const isSelected = selectedIds.has(product.id);
            const isMulti = activeCategory !== 'accommodation';
            const warning = cardWarningFor(
              product,
              isSelected,
              remainingCents,
              pickedTotals.currency
            );
            const isPreselected = preselectedIds.has(product.id);
            const iconOverlay =
              activeCategory === 'transportation'
                ? {
                    Icon: transportIconFor(product.title),
                    tone: isPreselected
                      ? ('primary' as const)
                      : ('muted' as const),
                  }
                : undefined;
            return (
              <CategoryCard
                iconOverlay={iconOverlay}
                isBestMatch={
                  activeCategory === 'accommodation' && isPreselected
                }
                key={product.id}
                multiSelect={isMulti}
                onToggle={(id) => toggleProduct(activeCategory, id)}
                product={{
                  ...product,
                  title: productDisplayTitle(product, activeCategory),
                }}
                provenance={
                  isPreselected
                    ? 'ai-picked'
                    : isBrowseMode
                      ? 'from-catalog'
                      : undefined
                }
                selected={isSelected}
                warning={warning}
                whyMatched={isPreselected ? product.whyMatched[0] : undefined}
              />
            );
          })
        )}
      </DayCategoryPanel>

      {gapsExpanded && visibleGaps.length > 0 && (
        <BriefGapsPanel
          gaps={visibleGaps}
          onAction={(gap, action) => {
            if (action === 'generate-anyway') dismissGap(gap.id);
          }}
        />
      )}

      {errorMessage && (
        <Alert
          icon={<AlertCircle aria-hidden="true" size={14} />}
          role="alert"
          variant="error"
        >
          {errorMessage}
        </Alert>
      )}

      <footer className={css.footer}>
        <BriefGapsDock
          count={visibleGaps.length}
          labels={dockLabels}
          onViewAll={() => setGapsExpanded((o) => !o)}
        />
        <div className={css.footerRow}>
          <button
            className={joinCss(css.footerBtn, css.footerBtnOutline)}
            disabled={isPending}
            onClick={close}
            type="button"
          >
            Preview email
          </button>
          <button
            className={joinCss(css.footerBtn, css.footerBtnPrimary)}
            data-testid="options-generate-itinerary"
            disabled={isGenerateDisabled}
            onClick={handleGenerate}
            type="button"
          >
            {isPending ? 'Generating…' : 'Generate itinerary'}
            {!isPending && <span aria-hidden="true">→</span>}
          </button>
        </div>
        <p className={css.footerHint}>{progressCaption}</p>
      </footer>
    </div>
  );
};
